import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

import DropdownPattern from '@mlb/react-patterns/components/Dropdown/dropdown.tsx';

import ContentCardsGroup from 'bdvp-common/src/components/ContentCardsGroup';
import Heading from 'bdvp-common/src/components/RowHeading';
import {
    useDictionary,
} from 'bdvp-common/src/hooks/config';
import {
    useDidUpdate,
    usePagination,
    useMainContainerQuery,
    useBackToState
} from 'bdvp-common/src/hooks';
import { getMediaPath } from 'bdvp-common/src/utils';
import { Tracking } from 'bdvp-tracking';

import Message from '../Message';

const Wrapper = styled.div`
    display: flex;
`;

const H2 = styled(Heading)`
    display: flex;
    min-height: 36px;
    justify-content: center;
    flex-direction: column;
    margin-bottom: 14px;
`;

const Dropdown = styled(DropdownPattern)`
    display: inline-block;
    margin-left: 30px;
    margin-bottom: 14px;
    z-index: 3;
`;

const buildSearchParams = (searchQuery, searchTitle, index, limit) => {

    return `?q=${searchQuery}&p=${Math.floor(index/limit)}&pt=${searchTitle}`;

};

const Innings = (props) => {

    const {
        inningOptions,
        selectedInning,
        setSelectedInning,
        gameLoading,
        searchQueryLoading,
        loadingMore,
        hasMore,
        list,
        gameError,
        fetchMoreData,
        searchQuery,
        searchQueryError,
        skipSearchQuery,
    } = props;

    const dictionary = useDictionary();
    const history = useHistory();
    const backToState = useBackToState('back_to_game_page');

    const title = dictionary.pitch_by_pitch ?? 'Pitch by Pitch';
    const searchTitle = `${title} - ${selectedInning?.label}`;

    const {
        limit,
        sizes,
    } = useMainContainerQuery(
        [
            'XL',
            'LG',
            'MD',
            'SM',
            'XS',
        ],
        [
            {
                limit: 20,
                sizes: {
                    xs: 20,
                },
            },
            {
                limit: 16,
                sizes: {
                    xs: 25,
                },
            },
            {
                limit: 15,
                sizes: {
                    xs: 33.33,
                },
            },
            {
                limit: 12,
                sizes: {
                    xs: 50,
                },
            },
            {
                limit: 6,
                sizes: {
                    xs: 100,
                },
            },
        ],
        {
            limit: 20,
            sizes: {
                xs: 100,
                sm: 50,
                md: 33.33,
                lg: 25,
                xl: 20,
            },
        }
    );

    const {
        paginatedList,
        hasMoreItems,
        getMoreItems,
        resetPagination,
        loadingPagination: listLoading,
    } = usePagination({
        list,
        limit,
        hasMore,
        fetchMore: fetchMoreData,
        isLoading: searchQueryLoading,
    });

    useDidUpdate(() => {

        resetPagination();

    }, [searchQuery]);

    const handleItemClick = useCallback((event, item, index) => {

        event.preventDefault();
        Tracking.trackGameIndexPageInningsOnItemClick(event, index, searchTitle);

        const mediaPath = getMediaPath(item);

        history.push(
            {
                pathname: `/${mediaPath}`,
                search: buildSearchParams(searchQuery, searchTitle, index, limit),
            },
            backToState
        );

    }, [
        history,
        searchQuery,
        searchTitle,
        limit,
        backToState,
    ]);

    const searchParamsCallback = useCallback((index) => {

        return buildSearchParams(searchQuery, searchTitle, index, limit);

    }, [
        searchQuery,
        searchTitle,
        limit,
    ]);

    const seeMoreButtonHandler = useCallback(() => {

        getMoreItems();
        Tracking.trackGameIndexPageInningsOnSeeMoreClick();

    }, [getMoreItems]);

    if (gameError || inningOptions.length === 0) {

        return null;

    }

    let type;

    if (searchQueryError) {

        type = 'search_error';

    } else if (!skipSearchQuery && !gameLoading && !listLoading && paginatedList.length === 0) {

        type = 'no_clips';

    }

    return (
        <>
            <Wrapper>
                <H2 loading={gameLoading} text={title} />
                {!gameLoading && (
                    <Dropdown
                        options={inningOptions}
                        defaultSelected={inningOptions[0]}
                        placeholder={dictionary.inning ?? 'Inning'}
                        width={'102px'}
                        menuWidth={'max-content'}
                        handlers={
                            {
                                onChange: ({ selected: updatedSelected }) => {

                                    Tracking.trackInningSelect(updatedSelected.value);
                                    setSelectedInning(updatedSelected);

                                },
                            }
                        }
                    />
                )}
            </Wrapper>
            {type ? (
                <Message type={type}/>
            ) : (
                <ContentCardsGroup
                    handleItemClick={handleItemClick}
                    hasMore={hasMoreItems}
                    isLoading={gameLoading || listLoading || skipSearchQuery}
                    limit={limit}
                    list={paginatedList}
                    loadingMore={loadingMore}
                    loadMoreButtonHandler={seeMoreButtonHandler}
                    orientation={'portrait'}
                    sizes={sizes}
                    searchParams={searchParamsCallback}
                />
            )}
        </>
    );

};

Innings.propTypes = {
    inningOptions: PropTypes.array.isRequired,
    selectedInning: PropTypes.object,
    setSelectedInning: PropTypes.func.isRequired,
    gameLoading: PropTypes.bool.isRequired,
    searchQueryLoading: PropTypes.bool.isRequired,
    loadingMore: PropTypes.bool.isRequired,
    list: PropTypes.array.isRequired,
    fetchMoreData: PropTypes.func.isRequired,
    hasMore: PropTypes.bool.isRequired,
    searchQuery: PropTypes.string.isRequired,
    gameError: PropTypes.object,
    searchQueryError: PropTypes.object,
    skipSearchQuery: PropTypes.bool.isRequired,
};

export default Innings;

